import { useState } from 'react';
import { devProgress, devProgressAction, devProgressPending, type DevProgress } from '@shared/dev-servers';
import type { TaskDevServers } from '@shared/types/factory';
import { useLoader } from '@/hooks/use-loader';
import { useNow } from '@/hooks/use-now';
import { describeApiError } from '@/lib/api-errors';
import { listDevServers } from '@/lib/dev-servers-api';
import { useTaskStore } from '@/stores/taskStore';

/**
 * While a Start, Stop or Tidy is on its way (on the machine, or until a report shows it: SF-T-231), the panel
 * looks this often besides the hub's `dev_servers` pushes (a fallback for a dropped socket); otherwise every 20 s.
 */
export const DEV_BUSY_POLL_MS = 15_000;
const IDLE_POLL_MS = 20_000;

/**
 * SF-T-209: the task's newest worktree and its dev servers, read again when `version` changes (the task changed:
 * a run moved), when the hub says they moved on (SF-T-231: each step of a Start, Stop or Tidy, its answer, and
 * the machine's reports while it settles), and every 20 s (15 s while one is on its way). Off with no `slug`.
 */
export function useTaskDevServers(taskId: string, slug: string | null, version: string) {
  const [busy, setBusy] = useState(false);
  const pushed = useTaskStore((s) => s.devServersTicks[taskId] ?? 0);
  const resync = useTaskStore((s) => s.resyncTick);
  const loader = useLoader(async (signal) => (slug ? listDevServers(taskId, { signal, project: slug }) : null), [taskId, slug, version, pushed, resync], {
    enabled: Boolean(slug),
    pollMs: busy ? DEV_BUSY_POLL_MS : IDLE_POLL_MS,
  });
  const res = loader.data;
  // Only this task's: while another task's answer is still on screen (moving between tasks), none.
  const found = res?.data?.dev_servers[0] ?? null;
  const fresh = found?.task.id === taskId ? found : null;
  // A failed look keeps the last good answer on screen (with the error beside it), rather than wiping the panel.
  const [lastGood, setLastGood] = useState<typeof fresh>(null);
  if (fresh && fresh !== lastGood) setLastGood(fresh);
  const failed = Boolean(slug && res && !res.data && !loader.loading);
  const devServers = fresh ?? (failed && lastGood?.task.id === taskId ? lastGood : null);
  const now = useNow(5_000, Boolean(devServers && devProgressAction(devServers)));
  const nowBusy = devServers?.action?.status === 'running' || Boolean(devServers && devProgressPending(devServers, now));
  if (nowBusy !== busy) setBusy(nowBusy);
  return { devServers, error: failed && res ? describeApiError(res) : null, reload: loader.reload };
}

/**
 * SF-T-231: how far the newest Start, Stop or Tidy of these dev servers has got (null when there's none on its
 * way), moving on with the clock so "waiting for the report" turns into a warning without a reload.
 */
export function useDevProgress(d: TaskDevServers): DevProgress | null {
  const watching = Boolean(devProgressAction(d)) && d.action?.status !== 'failed';
  const now = useNow(5_000, watching);
  return watching ? devProgress(d, now) : null;
}
